import { LitElement, css } from "lit-element";
import { html } from "lit-html";
import "./bds-treenode.js";
import { repeat } from "lit-html/directives/repeat";
import get from "lodash.get";

/**
 *  Folder Tree Component
 *
 *  @param { Array } nodes - Stores the entire tree stricture
 *  @param { Boolean } isMultiSelect - Determines if more that one node can be selected simultaneously
 *  @param { String } idPath - Path to key in tree data structure
 *  @param { String } labelPath - Path to label in tree data structure
 *  @param { String } dataPath - Path to additional data in the tree structure
 *  @param { Boolean } hasDragAndDrop - Enable nodes drag and drop
 *  @param { Object } styleConfig - used to set styling presets such as icon position
 *  Events
 *  - @fires dragAndDrop - fired when a node is dragged and dropped onto other node
 *  - @fires siblingDragAndDrop - fired when a node is dragged and dropped onto a node reorder space
 *  - @fires prevDragAndDrop - fired when a node is dragged and dropped onto space at the top of a list
 *  - @fires treeChange - fired when a node is clicked
 */

export class BDSTree extends LitElement {
  static get properties() {
    return {
      nodes: { type: Array },
      isMultiSelect: { type: Boolean },
      idPath: { type: String },
      labelPath: { type: String },
      dataPath: { type: String },
      data: { type: Object },
      initialNodeId: { type: String },
      hasUnselect: { type: Boolean },
      hasDragAndDrop: { type: Boolean },
      selectedKeys: { type: Array, state: true },
      expandedKeys: { type: Array, state: true },
      styleConfig: {type: Object},
    };
  }

  static get styles() {
    return css`
      ul {
        margin-left: 0;
        list-style: none;
        text-indent: -1.25rem;
        padding: 0;
      }

      :host::part(node) {
        margin-left: 0.5rem;
      }
    `;
  }

  constructor() {
    super();
    this.nodes = [];
    this.isMultiSelect = false;
    this.selectedKeys = [];
    this.expandedKeys = [];
    this.level = 0;
    this.idPath = "";
    this.labelPath = "";
    this.dataPath = "";
    this.hasUnselect = false;
    this.styleConfig = {hasIconBefore: true}
  }

  // set default state
  connectedCallback() {
    super.connectedCallback();
  }

  firstUpdated() {
    if (this.initialNodeId) {
      if (this.initialNodeId === "ROOT") {
        this.expandedKeys = ["ROOT"];
        this.selectedKeys = ["ROOT"];
      } else {
        let keys = this.getPath(
          { item: { externalId: "ROOT" }, children: this.nodes },
          [],
          this.initialNodeId || ""
        );
        if (keys) {
          keys?.shift();
        }
        this.expandedKeys = keys ? keys : [];
        this.selectedKeys = this.initialNodeId ? [this.initialNodeId] : [];
      }
    }
  }

  updated(changedProperties) {
    if (
      changedProperties.has("selectedKeys") &&
      changedProperties.get("selectedKeys")
    ) {
      this.dispatchTreeChange();
    }
  }

  dispatchTreeChange() {
    let options = {
      detail: {
        expandedKeys: this.expandedKeys,
        selectedKeys: this.selectedKeys,
        ...this.data,
      },
      bubbles: true,
      composed: true,
    };
    this.dispatchEvent(new CustomEvent("treeChange", options));
  }

  dispatchTreeClick() {
    let options = {
      detail: {
        expandedKeys: this.expandedKeys,
        selectedKeys: this.selectedKeys,
        ...this.data,
      },
      bubbles: true,
      composed: true,
    };
    this.dispatchEvent(new CustomEvent("treeClick", options));
  }

  dispatchDragAndDrop(dragKey, dropKey) {
    let options = {
      detail: {
        dragKey: dragKey,
        dropKey: dropKey,
      },
      bubbles: true,
      composed: true,
    };
    this.dispatchEvent(new CustomEvent("dragAndDrop", options));
  }

  dispatchSiblingDragAndDrop(dragKey, dropKey) {
    let options = {
      detail: {
        dragKey: dragKey,
        dropKey: dropKey,
      },
      bubbles: true,
      composed: true,
    };
    this.dispatchEvent(new CustomEvent("siblingDragAndDrop", options));
  }

  dispatchPrevDragAndDrop(dragKey, dropKey) {
    let options = {
      detail: {
        dragKey: dragKey,
        dropKey: dropKey,
      },
      bubbles: true,
      composed: true,
    };
    this.dispatchEvent(new CustomEvent("prevDragAndDrop", options));
  }

  getPath(node, path, nodeId) {
    if (get(node, this.idPath) === nodeId) {
      path.push(get(node, this.idPath));
      return path;
    } else {
      if (node.children && node.children.length > 0) {
        path.push(get(node, this.idPath));
        for (let child of node.children) {
          let result = this.getPath(child, path, nodeId);
          if (result) {
            return result;
          }
        }
        path.pop(get(node, this.idPath));
      } else {
        return null;
      }
    }
  }

  handleNodeSelected(e) {
    this._updateData(e);
    this._updateSelectedKeys(e);
    this.dispatchTreeClick();
  }

  handleNodeExpanded(e) {
    this._updateExpandedKeys(e)
  }

  handleDragAndDrop(e) {
    this.dispatchDragAndDrop(e.detail.dragKey, e.detail.dropKey);
  }

  handleSiblingDragAndDrop(e) {
    this.dispatchSiblingDragAndDrop(e.detail.dragKey, e.detail.dropKey);
  }

  // Helpers
  _updateData(e) {
    this.data = e.detail;
  }

  _updateExpandedKeys(e) {
    if (this.expandedKeys.includes(e.detail.key)) {
      this.expandedKeys = this.expandedKeys.filter(
        (key) => key !== e.detail.key
      );
    } else {
      this.expandedKeys = [...this.expandedKeys, e.detail.key];
    }
  }

  _updateSelectedKeys(e) {
    if (this.isMultiSelect) {
      if (this.selectedKeys.includes(e.detail.key)) {
        this.selectedKeys = this.selectedKeys.filter(
          (key) => key !== e.detail.key
        );
      } else {
        this.selectedKeys = [...this.selectedKeys, e.detail.key];
      }
    } else {
      if (this.selectedKeys.includes(e.detail.key)) {
        this.selectedKeys = this.hasUnselect ? [] : [e.detail.key];
      } else {
        this.selectedKeys = [e.detail.key];
      }
    }
  }

  render() {
    return html`
      <ul
        style="margin:0;"
        @nodeSelected=${this.handleNodeSelected}
        @nodeExpanded=${this.handleNodeExpanded}
        role="tree"
        aria-labelledby="tree_label"
      >
        ${repeat(
          this.nodes,
          (node) => get(node, this.idPath),
          (node) => html` <bds-treenode
            @dragAndDrop=${this.handleDragAndDrop}
            key=${get(node, this.idPath)}
            .isRootNode=${true}
            .level=${1}
            ?hasDragAndDrop=${this.hasDragAndDrop}
            label=${get(node, this.labelPath)}
            .data=${get(node, this.dataPath)}
            .nodes=${node.children}
            .path=${[get(node, this.idPath)]}
            .selectedKeys=${this.selectedKeys}
            .expandedKeys=${this.expandedKeys}
            .styleConfig=${this.styleConfig}
            labelPath=${this.labelPath}
            idPath=${this.idPath}
            dataPath=${this.dataPath}
          >
          </bds-treenode>`
        )}
      </ul>
    `;
  }
}

customElements.define("bds-tree", BDSTree);
